import { useEffect, useRef } from 'react';

export default function GardenSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(entry => {
        if (entry.isIntersecting) entry.target.classList.add('is-visible');
      }),
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="gardens" ref={sectionRef} className="py-20 md:py-28 bg-cream">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12 reveal">
          <div className="inline-flex items-center gap-2 bg-forest-50 text-forest rounded-full px-4 py-1.5 mb-4">
            <i className="ri-leaf-line text-sm w-4 h-4 flex items-center justify-center" />
            <span className="font-sans text-xs tracking-widest uppercase">Garden &amp; Landscape</span>
          </div>
          <h2 className="font-serif text-3xl md:text-5xl text-forest-dark leading-tight mb-4">
            A Botanical Masterpiece
          </h2>
          <p className="font-sans text-base text-gray-500 max-w-2xl mx-auto">
            Designed by a Canadian landscape architect, the gardens are a living asset — 45+ ornamental species, 18 fruit tree varieties, 5 precious woods, and a micro coffee farm of 450+ trees. Significantly more lush than any existing photos.
          </p>
        </div>

        {/* Main panoramic image */}
        <div className="relative rounded-2xl overflow-hidden mb-6 reveal" style={{ height: '460px' }}>
          <img
            src="https://montebrisa.com/wp-content/uploads/slider/cache/e87b5ead4f4e91b27730d30118f0cf46/top-bosque.jpg"
            alt="Montebrisa tropical forest and gardens"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/20 to-transparent" />
          <div className="absolute top-8 left-10">
            <p className="font-serif text-6xl md:text-8xl text-white font-light drop-shadow-lg leading-none">450+</p>
            <p className="font-sans text-white/80 text-lg mt-1">Coffee Trees</p>
          </div>
          <div className="absolute bottom-8 right-10 text-right">
            <p className="font-serif text-2xl text-white drop-shadow-md">45 Ornamental Species</p>
            <p className="font-sans text-white/70 text-sm mt-1">Designed by a Canadian Landscape Architect</p>
          </div>
          <div className="absolute bottom-8 left-10 flex items-center gap-2 bg-forest/80 backdrop-blur-sm rounded-full px-4 py-2">
            <i className="ri-leaf-line text-cream text-sm w-4 h-4 flex items-center justify-center" />
            <span className="font-sans text-cream text-sm">TripAdvisor GreenLeaders · Silver Certified</span>
          </div>
        </div>

        {/* Three columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="reveal rounded-2xl overflow-hidden h-60">
            <img
              src="https://montebrisa.com/wp-content/uploads/slider/cache/5947e46b126b8090d9dd6a8131d9c143/top-cafe.jpg"
              alt="Montebrisa micro coffee farm"
              className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="reveal rounded-2xl overflow-hidden h-60" style={{ transitionDelay: '0.1s' }}>
            <img
              src="https://montebrisa.com/wp-content/uploads/slider/cache/4828dc433b75b5d77a6e2fd3a6f18ddf/top7.jpg"
              alt="Montebrisa garden view"
              className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="reveal flex flex-col justify-center gap-4 p-6 bg-forest rounded-2xl" style={{ transitionDelay: '0.2s' }}>
            <h3 className="font-serif text-2xl text-white">Garden Highlights</h3>
            {[
              { icon: 'ri-plant-line', text: '45+ ornamental species' },
              { icon: 'ri-seedling-line', text: '450+ coffee trees (micro farm)' },
              { icon: 'ri-apple-line', text: '18 varieties of fruit trees' },
              { icon: 'ri-tree-line', text: '5 types of precious hardwoods' },
              { icon: 'ri-leaf-line', text: 'GreenLeaders Silver certified' },
            ].map(item => (
              <div key={item.text} className="flex items-center gap-3">
                <div className="w-7 h-7 flex items-center justify-center bg-white/15 rounded-lg shrink-0">
                  <i className={`${item.icon} text-cream text-sm`} />
                </div>
                <span className="font-sans text-white/85 text-sm">{item.text}</span>
              </div>
            ))}
            <p className="font-sans text-white/55 text-xs mt-2 leading-relaxed border-t border-white/20 pt-3">
              Note: vegetation is significantly more lush than existing website photos — the gardens have matured considerably since those images were taken.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
